/**
 * Developer.js — Public "About the Developer" page
 * Author: Markus.van.Kempen@gmail.com
 */

import React from 'react';
import SEO from './SEO';
import './AdminDocumentation.css';
import './Developer.css';

const MCP_PROJECTS = [
  {
    icon: '⚙️',
    label: 'IBM Maximo',
    tools: ['Maximo MCP Server', 'Maximo API Explorer (VS Code)', 'Maximo Cursor Explorer'],
    desc: 'Query Maximo data, manage work orders, browse Object Structures, and generate TypeScript / Python / cURL snippets — all from AI chat or your IDE.',
  },
  {
    icon: '🤖',
    label: 'Watsonx Orchestrate',
    tools: ['WxO Builder MCP Server', 'WxO Agent MCP', 'WxO ToolBox VS Code Extension'],
    desc: 'Manage tools, agents, connections, and flows in IBM Watsonx Orchestrate. Export, compare, and replicate across environments without leaving VS Code.',
  },
  {
    icon: '⚛️',
    label: 'IBM Quantum',
    tools: ['Quantum OpenQASM MCP Server', 'Quantum OpenQASM Assistant (VS Code)'],
    desc: 'Submit OpenQASM 2.0 circuits, poll jobs, fetch histograms, and manage IBM Quantum backends via natural language or IDE panel.',
  },
  {
    icon: '🎫',
    label: 'Zendesk',
    tools: ['Zendesk MCP Server'],
    desc: 'Create, search, assign, comment, and close Zendesk tickets through natural language. Works with Cursor, Claude Desktop, and watsonx Orchestrate.',
  },
  {
    icon: '☁️',
    label: 'IBM Code Engine',
    tools: ['Code Engine MCP Server'],
    desc: '43 tools for apps, jobs, builds, secrets, and configmaps on IBM Cloud Code Engine — deploy and monitor containerised workloads without a CLI.',
  },
];

export default function Developer() {
  return (
    <>
      <SEO
        title="About the Developer | Markus van Kempen"
        description="Meet Markus van Kempen — Full Stack Developer, Agentic AI Bridge builder, and architect of MCP servers for IBM Maximo, Watsonx Orchestrate, IBM Quantum & Zendesk."
        canonical="/developer"
        keywords="Markus van Kempen, full stack developer, MCP server, Model Context Protocol, Agentic AI, IBM Maximo, Watsonx Orchestrate, IBM Quantum, Zendesk, React, Node.js, Docker"
      />

      <div className="developer-page-container">

        {/* ── Hero ──────────────────────────────────────────── */}
        <div className="dev-hero">
          <div className="dev-hero-avatar" aria-hidden="true">👨‍💻</div>
          <h1 className="dev-hero-title">🎭 Meet the Wizard Behind the Curtain</h1>
          <h2 className="dev-hero-name">Markus Van Kempen</h2>
          <p className="dev-hero-subtitle">
            Full Stack Developer · Agentic AI Bridge Builder · System Architect<br />
            <em>(aka Code Whisperer, Bug Exterminator &amp; MCP Wrangler)</em>
          </p>
          <p className="dev-hero-bio">
            Based in Toronto, Markus ships open-source <strong>Model Context Protocol (MCP) servers</strong> and{' '}
            <strong>IDE extensions</strong> that bridge IBM enterprise systems — Maximo, Watsonx Orchestrate,
            IBM Quantum, Zendesk — to AI assistants like Cursor, VS Code, and Claude. When he's not
            wrestling with Docker containers, he's building the agentic AI tooling that makes enterprise
            data actually usable from a chat prompt.
          </p>
          <blockquote className="dev-quote">
            "I don't always write documentation, but when I do, I make it funny."
            <cite>— Markus, probably</cite>
          </blockquote>

          <div className="dev-hero-badges">
            <span className="dev-badge">13 Extensions &amp; tools</span>
            <span className="dev-badge">7 MCP servers</span>
            <span className="dev-badge">5+ AI IDEs supported</span>
            <span className="dev-badge">Apache 2.0 Open Source</span>
          </div>
        </div>

        {/* ── Contact ───────────────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">📫 How to Reach This Magical Being</h2>
          <div className="contact-grid dev-contact-grid">
            <a href="mailto:markus.van.kempen@gmail.com" className="contact-item dev-contact-link">
              <span className="contact-icon">📧</span>
              <div className="contact-details">
                <strong>Email</strong>
                <span className="dev-contact-value">markus.van.kempen@gmail.com</span>
                <p><small>Preferred method. Response time: faster than a database query, slower than instant noodles.</small></p>
              </div>
            </a>
            <a href="https://markusvankempen.github.io/" target="_blank" rel="noopener noreferrer" className="contact-item dev-contact-link dev-contact-featured">
              <span className="contact-icon">🚀</span>
              <div className="contact-details">
                <strong>Agentic AI Bridge — Portfolio</strong>
                <span className="dev-contact-value">markusvankempen.github.io</span>
                <p><small>MCP servers, VS Code/Cursor extensions, IBM enterprise tools — all open-source, all Apache 2.0.</small></p>
              </div>
            </a>
            <a href="https://www.linkedin.com/in/markusvankempen/" target="_blank" rel="noopener noreferrer" className="contact-item dev-contact-link">
              <span className="contact-icon">💼</span>
              <div className="contact-details">
                <strong>LinkedIn</strong>
                <span className="dev-contact-value">linkedin.com/in/markusvankempen</span>
                <p><small>Professional networking, code reviews, and sharing memes about JavaScript.</small></p>
              </div>
            </a>
            <a href="https://markusvankempen.wordpress.com/" target="_blank" rel="noopener noreferrer" className="contact-item dev-contact-link">
              <span className="contact-icon">📝</span>
              <div className="contact-details">
                <strong>Blog</strong>
                <span className="dev-contact-value">markusvankempen.wordpress.com</span>
                <p><small>Where code meets creativity and technical articles meet terrible puns.</small></p>
              </div>
            </a>
          </div>
        </section>

        {/* ── Agentic AI Bridge spotlight ───────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">🤖 Agentic AI Bridge</h2>
          <div className="doc-card dev-ai-card">
            <p className="dev-ai-lead">
              Markus builds the <strong>Agentic AI Bridge</strong> — a suite of open-source MCP servers and
              IDE extensions that connect AI agents directly to enterprise systems of record.
              The pattern is always the same: an AI assistant calls typed MCP tools → the server talks to
              platform APIs → you get real enterprise data in your chat prompt, securely and repeatably.
            </p>
            <div className="dev-ai-flow">
              <div className="dev-ai-flow-box dev-ai-flow-ide">Cursor · VS Code · Claude · Antigravity</div>
              <div className="dev-ai-flow-arrow">MCP stdio / SSE</div>
              <div className="dev-ai-flow-box dev-ai-flow-mcp">@markusvankempen/* MCP servers</div>
              <div className="dev-ai-flow-arrow">REST / OpenAPI</div>
              <div className="dev-ai-flow-box dev-ai-flow-enterprise">Maximo · WxO · Quantum · Zendesk · IBM Cloud</div>
            </div>
          </div>

          <div className="dev-mcp-grid">
            {MCP_PROJECTS.map(p => (
              <div key={p.label} className="dev-mcp-card">
                <div className="dev-mcp-header">
                  <span className="dev-mcp-icon">{p.icon}</span>
                  <h4 className="dev-mcp-label">{p.label}</h4>
                </div>
                <p className="dev-mcp-desc">{p.desc}</p>
                <div className="dev-mcp-tools">
                  {p.tools.map(t => <span key={t} className="dev-mcp-tool">{t}</span>)}
                </div>
              </div>
            ))}
          </div>

          <div className="dev-ai-cta">
            <a href="https://markusvankempen.github.io/" target="_blank" rel="noopener noreferrer" className="contact-button">
              🚀 Explore All Projects
            </a>
            <a href="https://github.com/markusvankempen" target="_blank" rel="noopener noreferrer" className="contact-button dev-btn-secondary">
              GitHub — @markusvankempen
            </a>
          </div>
        </section>

        {/* ── Community projects ────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">🌍 Community Projects</h2>
          <p className="dev-section-sub"><em>Open tools built for real communities — not just enterprise platforms.</em></p>
          <div className="dev-community-grid">
            <a href="https://ferrylight.online/about" target="_blank" rel="noopener noreferrer" className="dev-community-card">
              <div className="dev-community-header">
                <span className="dev-community-icon">⛴️</span>
                <div>
                  <h4 className="dev-community-title">FerryLight</h4>
                  <span className="dev-community-url">ferrylight.online</span>
                </div>
              </div>
              <p className="dev-community-desc">
                Real-time Englishtown Ferry status, schedule, and operations tracker for Cape Breton, Nova Scotia.
                Because nobody wants to drive to the ferry only to find it's not running.
              </p>
              <span className="dev-community-cta">View Project →</span>
            </a>
          </div>
        </section>

        {/* ── What Markus does ──────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">🛠️ What Markus Actually Does <em>(According to Markus)</em></h2>
          <div className="feature-grid">
            <div className="feature-item">
              <h4>🤖 Agentic AI &amp; MCP</h4>
              <p><em>"Teaching AI assistants to do enterprise things"</em></p>
              <ul>
                <li>Model Context Protocol server design (stdio and SSE transports)</li>
                <li>VS Code &amp; Cursor extension development (published to Marketplace)</li>
                <li>IBM Code Engine SSE gateway deployments (server-side secrets, shared URLs)</li>
                <li>Typed tool APIs with strong OpenAPI/REST integration</li>
              </ul>
            </div>
            <div className="feature-item">
              <h4>🎨 Frontend Development</h4>
              <p><em>"Making things look pretty and actually work"</em></p>
              <ul>
                <li>React.js application architecture (because vanilla JS is too vanilla)</li>
                <li>Responsive UI/UX design (yes, it works on your phone too)</li>
                <li>Component library development (DRY: Don't Repeat Yourself, or cry)</li>
                <li>Full theme systems with CSS Variables (six themes and counting)</li>
              </ul>
            </div>
            <div className="feature-item">
              <h4>⚙️ Backend Development</h4>
              <p><em>"Making the magic happen behind the scenes"</em></p>
              <ul>
                <li>Node.js/Express API design (RESTful, but sometimes restless)</li>
                <li>Database schema design (normalization is a rule, not a suggestion)</li>
                <li>Authentication systems (keeping the bad guys out, one JWT at a time)</li>
                <li>RESTful API implementation (because SOAP is for washing hands)</li>
              </ul>
            </div>
            <div className="feature-item">
              <h4>🐳 Infrastructure &amp; DevOps</h4>
              <p><em>"Where containers go to play nicely together"</em></p>
              <ul>
                <li>Docker containerization (it works on my machine™, now in a box)</li>
                <li>Traefik reverse proxy (the traffic director nobody asked for but everyone needs)</li>
                <li>IBM Code Engine deployments (cloud-native serverless containers)</li>
                <li>SSL/TLS automation (keeping Let's Encrypt happy since 2025)</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ── Tech stack ────────────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">📚 Technologies Markus Claims to Know</h2>
          <p className="dev-section-sub"><em>Disclaimer: Actual mastery may vary. Google and Stack Overflow are always just one tab away.</em></p>
          <div className="dev-tech-grid">
            <div className="dev-tech-group">
              <h4>🤖 Agentic AI &amp; MCP</h4>
              <p className="dev-tech-desc"><small>The new frontier (and it moves fast)</small></p>
              <div className="tech-tags">
                {['Model Context Protocol', 'MCP stdio', 'MCP SSE', 'Cursor Extensions', 'VS Code Extensions', 'IBM Watsonx Orchestrate', 'IBM Maximo', 'IBM Quantum', 'Code Engine', 'TypeScript'].map(t => (
                  <span key={t} className="tech-tag">{t}</span>
                ))}
              </div>
            </div>
            <div className="dev-tech-group">
              <h4>🖥️ Frontend</h4>
              <p className="dev-tech-desc"><small>The stuff you see and hopefully like</small></p>
              <div className="tech-tags">
                {['React.js', 'JavaScript (ES6+)', 'HTML5', 'CSS3', 'React Router', 'Responsive Design', 'CSS Variables / Theming'].map(t => (
                  <span key={t} className="tech-tag">{t}</span>
                ))}
              </div>
            </div>
            <div className="dev-tech-group">
              <h4>⚙️ Backend</h4>
              <p className="dev-tech-desc"><small>Where the real magic happens (or breaks)</small></p>
              <div className="tech-tags">
                {['Node.js', 'Express.js', 'RESTful APIs', 'SQLite', 'PostgreSQL', 'JWT Authentication', 'Multer', 'Nodemailer'].map(t => (
                  <span key={t} className="tech-tag">{t}</span>
                ))}
              </div>
            </div>
            <div className="dev-tech-group">
              <h4>🐳 DevOps &amp; Infrastructure</h4>
              <p className="dev-tech-desc"><small>Making computers do things automatically</small></p>
              <div className="tech-tags">
                {['Docker', 'Docker Compose', 'Traefik', 'IBM Code Engine', 'Linux (Ubuntu)', "Let's Encrypt", 'Docker Mail Server'].map(t => (
                  <span key={t} className="tech-tag">{t}</span>
                ))}
              </div>
            </div>
            <div className="dev-tech-group">
              <h4>🔨 Tools &amp; Platforms</h4>
              <p className="dev-tech-desc"><small>The things that make development less painful</small></p>
              <div className="tech-tags">
                {['Git', 'VS Code', 'Cursor AI', 'Node-RED', 'Portainer', 'pgAdmin', 'Postman', 'npm'].map(t => (
                  <span key={t} className="tech-tag">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Timeline ──────────────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">🎯 Project Timeline &amp; Milestones</h2>
          <p className="dev-section-sub"><em>A story of triumph, tears, and "why did I think that was a good idea?" moments.</em></p>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-marker">✅</div>
              <div className="timeline-content">
                <h4>Phase 1: Cranford Publications Foundation</h4>
                <p><strong>Status:</strong> Complete (after only 47 rewrites)</p>
                <ul>
                  <li>Full-stack e-commerce platform — React + Node.js + SQLite</li>
                  <li>Stripe &amp; PayPal integration, order management, wholesale pricing</li>
                  <li>JWT authentication, role-based access control</li>
                  <li>Docker + Traefik production deployment on VPS</li>
                </ul>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">✅</div>
              <div className="timeline-content">
                <h4>Phase 2: Content &amp; Admin Platform</h4>
                <p><strong>Status:</strong> Complete (mostly. CSS is hard.)</p>
                <ul>
                  <li>CMS with tune notation pages, ABC.js rendering, and audio playback</li>
                  <li>Admin dashboard — products, orders, users, assets, pages, newsletters</li>
                  <li>Six-theme system with full CSS Variable coverage</li>
                  <li>PWA with service worker audio caching</li>
                </ul>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">✅</div>
              <div className="timeline-content">
                <h4>Phase 3: Agentic AI Bridge — First Wave</h4>
                <p><strong>Status:</strong> Shipped — open-source on GitHub</p>
                <ul>
                  <li>7 MCP servers for Maximo, Watsonx Orchestrate, IBM Quantum, Zendesk, Code Engine</li>
                  <li>13 VS Code / Cursor extensions published to VS Code Marketplace &amp; Open VSX</li>
                  <li>IBM Code Engine SSE gateways for remote MCP with shared team URLs</li>
                  <li>Apache 2.0 — contributions and issues welcome</li>
                </ul>
              </div>
            </div>
            <div className="timeline-item">
              <div className="timeline-marker">🔄</div>
              <div className="timeline-content">
                <h4>Phase 4: AI-Native Everything <em>(The Never-Ending Story)</em></h4>
                <p><strong>Status:</strong> In Progress</p>
                <ul>
                  <li>More MCP servers for additional IBM and enterprise platforms</li>
                  <li>Agentic workflows — multi-step AI automation across Maximo, WxO, and Quantum</li>
                  <li>PostgreSQL migration for Cranford Publications (SQLite, it's been real)</li>
                  <li>Performance optimization, because slow is not a feature</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ── Support options ───────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">💡 Need Help? Markus Has Your Back</h2>
          <p className="dev-section-sub">For technical support, bug reports, feature requests, or existential questions about why your code isn't working:</p>
          <div className="support-options">
            <div className="support-option">
              <h4>📧 Email Support</h4>
              <p>Detailed inquiries, technical issues, project discussions. Also accepts pizza orders, but not payment.</p>
              <a href="mailto:markus.van.kempen@gmail.com" className="contact-button">Email Markus</a>
              <p><small>Fun fact: Markus responds faster to emails with subject lines that include emojis. 🎯</small></p>
            </div>
            <div className="support-option">
              <h4>🚀 Agentic AI Portfolio</h4>
              <p>Browse all open-source MCP servers and IDE extensions. One-click <code>mcp.json</code> setup, Cursor Directory listings, and remote SSE deploys.</p>
              <a href="https://markusvankempen.github.io/" target="_blank" rel="noopener noreferrer" className="contact-button">markusvankempen.github.io</a>
            </div>
            <div className="support-option">
              <h4>💼 LinkedIn</h4>
              <p>Professional networking, code reviews, and sharing developer memes that only other developers find funny.</p>
              <a href="https://www.linkedin.com/in/markusvankempen/" target="_blank" rel="noopener noreferrer" className="contact-button">Connect on LinkedIn</a>
            </div>
          </div>
        </section>

        {/* ── Fun facts ─────────────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">🎉 Fun Facts About Markus</h2>
          <div className="dev-fun-facts">
            {[
              ['🏃', 'Runs on coffee and code, in that order'],
              ['🤖', 'Has shipped MCP servers for IBM Quantum, Maximo, WxO, Zendesk, and Code Engine — all open source'],
              ['🐛', 'Has debugged more issues than he cares to admit (or remember)'],
              ['📚', 'Loves reading technical documentation — yes, he\'s that kind of person'],
              ['🎸', 'Has been known to play music when not writing code (allegedly)'],
              ['☕', 'Considers "too much coffee" a myth perpetuated by non-developers'],
              ['💻', 'Believes the best code doesn\'t need comments (but adds them anyway because reality is harsh)'],
              ['🔧', 'Once spent 3 hours debugging a typo (we\'ve all been there)'],
            ].map(([emoji, fact]) => (
              <div key={fact} className="dev-fact-item">
                <span className="dev-fact-emoji">{emoji}</span>
                <span>{fact}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── Acknowledgements ──────────────────────────────── */}
        <section className="dev-section">
          <h2 className="dev-section-title">🙏 Acknowledgments</h2>

          {/* Paul Cranford tribute */}
          <div className="doc-card dev-tribute-card">
            <p className="dev-tribute-lead">
              🎻 This site was built with love for our friend{' '}
              <strong>Paul Cranford</strong> — the <em>Purveyor of Fine Fiddle Tunes since 1979</em>.
            </p>
            <p>
              Paul has spent decades collecting, publishing, and preserving the traditional music of Cape Breton
              and beyond. This project exists not as a business exercise, but as an act of friendship — to help
              Paul's extraordinary body of work reach the people who love it, and to make sure those tunes are
              never lost. It's a project about <strong>community and friendship</strong>, plain and simple.
            </p>
          </div>

          {/* Stephanie Conn */}
          <div className="doc-card dev-tribute-card" style={{ marginTop: '1rem' }}>
            <p className="dev-tribute-lead">
              🌟 A massive thank you to{' '}
              <a href="https://stephanieconn.com/" target="_blank" rel="noopener noreferrer" className="dev-tribute-link">
                Stephanie Conn
              </a>
            </p>
            <p>
              Stephanie is a writer, editor, producer, and passionate champion of Cape Breton music and culture —
              the perfect person to have in your corner when building a site like this. She was significantly
              involved in both the testing and the building of this site: her sharp eye, patience, and
              real-world feedback were invaluable at every stage. Bugs were caught, ideas were shaped, and the
              whole thing is better for her involvement. She deserves far more than a bullet point. ❤️
            </p>
          </div>

          <div className="doc-card" style={{ marginTop: '1rem' }}>
            <p><strong>And further thanks to:</strong></p>
            <ul>
              <li>The open-source community for the excellent tools and frameworks. You're the real MVPs.</li>
              <li>The Cursor AI team — for building the IDE that makes agentic development actually fun.</li>
              <li>All contributors who provided feedback — even the ones Markus initially disagreed with (he was probably wrong).</li>
              <li>☕ Coffee, for existing and making long coding sessions possible.</li>
              <li>Stack Overflow, for answering questions Markus didn't even know he had.</li>
            </ul>
          </div>
        </section>

        {/* ── Important notes ───────────────────────────────── */}
        <section className="dev-section">
          <div className="doc-card important">
            <h3>⚠️ Important Notes <em>(Read These or Suffer the Consequences)</em></h3>
            <ul>
              <li><strong>Response Time:</strong> Typically 24–48 hours for email. Emergency? Send pizza. Or donuts. Both work.</li>
              <li><strong>Documentation:</strong> Check the docs first. Markus spent time writing them. (He's watching.)</li>
              <li><strong>Bug Reports:</strong> Include steps to reproduce, error messages, and screenshots. "It's broken" is not a bug report.</li>
              <li><strong>Feature Requests:</strong> Describe the use case in detail. "Make it cooler" is not actionable. (But nice try.)</li>
              <li><strong>Security:</strong> Never share credentials via email. Markus doesn't want your passwords. Really.</li>
              <li><strong>Coffee Preferences:</strong> Dark roast preferred, but he'll accept any caffeinated beverage in a crisis.</li>
            </ul>
          </div>
        </section>

      </div>
    </>
  );
}
